import React, { ChangeEvent, useCallback, useEffect, useState } from 'react'

// ** MUI Imports
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import { FormControl, Tooltip } from '@material-ui/core'
import LayoutContainer from 'src/components/common/containers/LayoutContainer'
import SaveNotification from 'src/components/common/SaveNotification'
import { putRequest } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'
import Alerts from 'src/components/common/feedback-and-dialog/Alerts'
import { useLoader } from 'src/lib/contexts/LoaderContext'

interface ShopSettingsProps {
  shopData: any
}

interface ShopDetails {
  shopUrl: string
  storefrontToken: string
  accessToken: string
}

const ShopSettings: React.FC<ShopSettingsProps> = ({ shopData }) => {
  const { data, is_configured: isConfigured } = shopData
  const { setLoaderProperties } = useLoader()

  const initShopDetails = useCallback(() => {
    return {
      shopUrl: data?.shop_url ?? '',
      storefrontToken: data?.storefront_token ?? '',
      accessToken: data?.access_token ?? ''
    }
  }, [data])

  const [shopDetails, setShopDetails] = useState<ShopDetails>(initShopDetails())
  const [showSaveStrip, setShowSaveStrip] = useState<boolean>(false)
  const [openAlert, setOpenAlert] = useState<string>('')

  const updateField = (key: string, value: string) => {
    setShopDetails((oldValues: any) => ({ ...oldValues, [key]: value }))
    setShowSaveStrip(true)
  }

  const handleUpdateShopDetails = async () => {
    setLoaderProperties({ isLoaderVisible: true })
    if (!Boolean(shopDetails.accessToken) || !Boolean(shopDetails.shopUrl) || !Boolean(shopDetails.storefrontToken)) {
      setOpenAlert('incomplete_form')
      return
    }
    const payload = {
      shop: shopDetails.shopUrl,
      access_token: shopDetails.accessToken,
      storefront_token: shopDetails.storefrontToken
    }

    try {
      const response: any = await putRequest(`/attributes/v1/settings/shop-details`, apiURI.FLO_CHECKOUT, payload)
      if (response.error) {
        setOpenAlert(response.error)
        setLoaderProperties({ isLoaderVisible: false })
        return
      }
      setOpenAlert('success')
      setShowSaveStrip(false)
    } catch (e) {
      console.error(e)
      setOpenAlert('error')
    }
    setLoaderProperties({ isLoaderVisible: false })
  }

  const handleResetShopDetails = () => {
    const initSettings = initShopDetails()
    setShopDetails(initSettings)
    setShowSaveStrip(false)
  }

  const handleCloseAlert = (event: any, reason: any) => {
    if (reason === 'clickaway') {
      return
    }
    setOpenAlert('')
  }

  const getAlertMsg = useCallback(() => {
    if (openAlert === 'success') return 'Shop data updated successfully!'
    if (openAlert === 'incomplete_form') return 'Please fill all the fields.'
    return openAlert
  }, [openAlert])

  if (!Boolean(shopData)) return <></>
  return (
    <>
      <FormControl style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16
          }}
        >
          <LayoutContainer>
            <Grid display={'flex'} alignItems={'start'} width={'100%'} flexDirection='column'>
              <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }} fontWeight={500}>
                Shop URL
              </Typography>
              <Tooltip title='A Shopflo admin has configured this for you, if this is incorrect, please reach out to Shopflo support.'>
                <TextField
                  disabled={true}
                  fullWidth
                  type='text'
                  size='small'
                  variant='outlined'
                  sx={{ maxWidth: '100%' }}
                  value={shopDetails.shopUrl}
                  placeholder='www.yourdomain.com'
                  inputProps={{ 'aria-label': 'controlled' }}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => updateField('shopUrl', event.target.value)}
                />
              </Tooltip>
            </Grid>
          </LayoutContainer>

          <LayoutContainer>
            <>
              <Typography variant='body1' sx={{ fontWeight: 500, color: '#1A1A1A' }}>
                Credentials
              </Typography>

              <Grid display={'flex'} alignItems={'start'} width={'100%'} flexDirection='column' gap='1rem'>
                <Grid display={'flex'} alignItems={'start'} width={'100%'} flexDirection='column'>
                  <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }} fontWeight={500}>
                    Admin API access token
                  </Typography>
                  <Tooltip title='A Shopflo admin has configured this for you, if this is incorrect, please reach out to Shopflo support.'>
                    <TextField
                      fullWidth
                      disabled={Boolean(isConfigured) ? true : false}
                      type='text'
                      size='small'
                      variant='outlined'
                      sx={{ maxWidth: '100%' }}
                      value={
                        shopDetails.accessToken
                          ? `${shopDetails.accessToken.slice(0, 10)}${'*'.repeat(shopDetails.accessToken.length - 10)}`
                          : ''
                      }
                      inputProps={{ 'aria-label': 'controlled' }}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        updateField('accessToken', event.target.value)
                      }
                    />
                  </Tooltip>
                </Grid>
                <Grid display={'flex'} alignItems={'start'} width={'100%'} flexDirection='column'>
                  <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }} fontWeight={500}>
                    Storefront API access token
                  </Typography>
                  <Tooltip title='A Shopflo admin has configured this for you, if this is incorrect, please reach out to Shopflo support.'>
                    <TextField
                      disabled={Boolean(isConfigured) ? true : false}
                      fullWidth
                      type='text'
                      size='small'
                      variant='outlined'
                      sx={{ maxWidth: '100%' }}
                      value={
                        shopDetails.storefrontToken
                          ? `${shopDetails.storefrontToken.slice(0, 4)}${'*'.repeat(
                              shopDetails.storefrontToken.length - 4
                            )}`
                          : ''
                      }
                      inputProps={{ 'aria-label': 'controlled' }}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        updateField('storefrontToken', event.target.value)
                      }
                    />
                  </Tooltip>
                </Grid>
              </Grid>
            </>
          </LayoutContainer>
        </div>
      </FormControl>

      <SaveNotification onSave={handleUpdateShopDetails} onDiscard={handleResetShopDetails} isVisible={showSaveStrip} />

      {openAlert.length ? (
        <Alerts
          open={true}
          duration={3000}
          closeHandler={handleCloseAlert}
          severity={openAlert === 'success' ? 'success' : 'error'}
          message={getAlertMsg()}
        />
      ) : null}
    </>
  )
}

export default ShopSettings
